import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileText, Library, Lock } from "lucide-react";
import { DECISION_STATUS, ENCRYPTED_TITLE_PLACEHOLDER, EVIDENCE_STATE, HYPOTHESIS_STATE, NOTE_KIND } from "@brainlog/shared";
import { get } from "@/lib/api";
import { openNote, openPaper } from "@/lib/store";
import type { Workspace } from "@/lib/types";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { KindIcon, kindLabel } from "@/features/notes/kinds";
import { useTree } from "@/lib/queries";
import { useKeyring } from "@/features/crypto/keyring";
import { decTitle } from "@/features/crypto/ops";
import { NoteTitle } from "@/features/crypto/UnlockPanel";

interface SearchResult {
  notes: { id: string; title: string; kind: string; tags: string[]; snippet: string; status: string | null; encTitle?: string | null; encryptionScopeId?: string | null }[];
  pages: { itemId: string; attachmentId: string; title: string; page: number; pageLabel: string | null; snippet: string }[];
  method: string;
}

const STATUS_OPTIONS = [
  ...EVIDENCE_STATE.values.map((v) => ({ v, l: `Result: ${EVIDENCE_STATE.labels[v]}` })),
  ...HYPOTHESIS_STATE.values.map((v) => ({ v, l: `Hypothesis: ${HYPOTHESIS_STATE.labels[v]}` })),
  ...DECISION_STATUS.values.map((v) => ({ v, l: `Decision: ${DECISION_STATUS.labels[v]}` })),
];

/** Snippets are rendered as text with «…» markers for matches — never as HTML. */
function Snippet({ text }: { text: string }) {
  const parts = text.split(/(«[^»]*»)/g);
  return (
    <span className="text-xs text-muted-foreground">
      {parts.map((p, i) => (p.startsWith("«") ? <mark key={i} className="rounded bg-warning/25 px-0.5 text-foreground">{p.slice(1, -1)}</mark> : <span key={i}>{p}</span>))}
    </span>
  );
}

export function SearchDialog({ open, onOpenChange, workspace }: { open: boolean; onOpenChange(o: boolean): void; workspace?: Workspace }) {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [kind, setKind] = useState("");
  const [tag, setTag] = useState("");
  const [status, setStatus] = useState("");
  const listRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 200);
    return () => clearTimeout(t);
  }, [q]);
  const params = useMemo(() => new URLSearchParams({ q: debounced, kind, tag, status }).toString(), [debounced, kind, tag, status]);
  const enabled = !!workspace && open && (debounced.trim().length > 1 || !!kind || !!tag || !!status);
  const res = useQuery({
    queryKey: ["search", workspace?.id, params],
    queryFn: () => get<SearchResult>(`/api/workspaces/${workspace!.id}/search?${params}`),
    enabled,
  });

  // Encrypted notes are invisible to server search; their titles are matched here, in the browser.
  const tree = useTree(open ? workspace?.id : undefined);
  const unlocked = useKeyring((st) => st.unlocked);
  const encTitles = useQuery({
    queryKey: ["enc-titles", workspace?.id, tree.dataUpdatedAt, unlocked.join(",")],
    enabled: open && !!tree.data && unlocked.length > 0,
    gcTime: 0,
    queryFn: async () => {
      const out: { id: string; kind: string; title: string; tags: string[] }[] = [];
      for (const n of tree.data!.notes) {
        if (!n.encryptionScopeId || !n.encTitle || !unlocked.includes(n.encryptionScopeId)) continue;
        try {
          out.push({ id: n.id, kind: n.kind, tags: n.tags, title: await decTitle(n.encryptionScopeId, n.id, n.encTitle) });
        } catch {
          /* locked meanwhile */
        }
      }
      return out;
    },
  });
  const needle = debounced.trim().toLowerCase();
  const encMatches = (encTitles.data ?? []).filter(
    (n) => needle.length > 1 && n.title.toLowerCase().includes(needle) && (!kind || n.kind === kind) && (!tag || n.tags.includes(tag)) && !status,
  );

  const go = (fn: () => void) => {
    fn();
    onOpenChange(false);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const items = Array.from(listRef.current?.querySelectorAll<HTMLElement>("button") ?? []);
    if (!items.length) return;
    e.preventDefault();
    const idx = items.indexOf(document.activeElement as HTMLElement);
    const next = idx < 0 ? 0 : Math.max(0, Math.min(items.length - 1, idx + (e.key === "ArrowDown" ? 1 : -1)));
    items[next].focus();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-[12%] max-h-[80vh] translate-y-0 overflow-hidden sm:max-w-2xl" onKeyDown={onKey}>
        <DialogHeader>
          <DialogTitle>Search {workspace ? `“${workspace.name}”` : ""}</DialogTitle>
          <DialogDescription>Finds the words you type in notes, research records and the text of papers in this {workspace?.kind === "notebook" ? "notebook" : "project"}.</DialogDescription>
        </DialogHeader>
        <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search words or phrases…" aria-label="Search query" />
        <div className="flex flex-wrap gap-2 text-sm">
          <select aria-label="Filter by record type" value={kind} onChange={(e) => setKind(e.target.value)} className="rounded-md border bg-background px-2 py-1">
            <option value="">Any type</option>
            {NOTE_KIND.values.map((k) => (
              <option key={k} value={k}>
                {NOTE_KIND.labels[k]}
              </option>
            ))}
          </select>
          <select aria-label="Filter by status" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border bg-background px-2 py-1">
            <option value="">Any status</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s.l} value={s.v}>
                {s.l}
              </option>
            ))}
          </select>
          <Input aria-label="Filter by tag" placeholder="#tag" value={tag} onChange={(e) => setTag(e.target.value.replace(/^#/, ""))} className="h-8 w-32" />
        </div>
        <div className="min-h-0 overflow-y-auto" style={{ maxHeight: "50vh" }}>
          {!enabled && <p className="py-4 text-sm text-muted-foreground">Type at least two characters or choose a filter.</p>}
          {res.isFetching && <p className="py-2 text-sm text-muted-foreground" role="status">Searching…</p>}
          {res.isError && <p className="py-2 text-sm text-destructive">Search failed. Try again.</p>}
          {res.data && (
            <ul ref={listRef} className="space-y-1" aria-label="Search results">
              {res.data.notes.map((n) => (
                <li key={n.id}>
                  <button type="button" className="w-full rounded-md px-2 py-1.5 text-left hover:bg-accent focus:bg-accent" onClick={() => go(() => openNote(n.id, n.title, workspace?.id))}>
                    <span className="flex items-center gap-1.5 text-sm font-medium">
                      <KindIcon kind={n.kind} /> <NoteTitle note={n} />
                      <span className="text-xs font-normal text-muted-foreground">· {kindLabel(n.kind)}</span>
                      {n.tags.map((t) => (
                        <span key={t} className="text-xs font-normal text-muted-foreground">#{t}</span>
                      ))}
                    </span>
                    {n.snippet && <Snippet text={n.snippet} />}
                  </button>
                </li>
              ))}
              {res.data.pages.map((p) => (
                <li key={`${p.attachmentId}-${p.page}`}>
                  <button type="button" className="w-full rounded-md px-2 py-1.5 text-left hover:bg-accent focus:bg-accent" onClick={() => go(() => openPaper(p.itemId, p.title, p.page, workspace?.id))}>
                    <span className="flex items-center gap-1.5 text-sm font-medium">
                      <Library className="size-4 text-muted-foreground" aria-hidden /> {p.title}
                      <span className="text-xs font-normal text-muted-foreground">
                        · PDF page {p.page}
                        {p.pageLabel && p.pageLabel !== String(p.page) ? ` (printed ${p.pageLabel})` : ""}
                      </span>
                    </span>
                    <Snippet text={p.snippet} />
                  </button>
                </li>
              ))}
              {encMatches.map((n) => (
                <li key={`enc-${n.id}`}>
                  <button type="button" className="w-full rounded-md px-2 py-1.5 text-left hover:bg-accent focus:bg-accent" onClick={() => go(() => openNote(n.id, ENCRYPTED_TITLE_PLACEHOLDER, workspace?.id))}>
                    <span className="flex items-center gap-1.5 text-sm font-medium">
                      <KindIcon kind={n.kind} /> <Lock className="size-3 text-muted-foreground" aria-hidden /> {n.title}
                      <span className="text-xs font-normal text-muted-foreground">· {kindLabel(n.kind)} · encrypted — title matched in this browser</span>
                    </span>
                  </button>
                </li>
              ))}
              {!res.data.notes.length && !res.data.pages.length && !encMatches.length && (
                <li className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
                  <FileText className="size-4" aria-hidden /> No matches.
                </li>
              )}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
